import {
    DropDownList, DropDownListBlurEvent,
    DropDownListChangeEvent, DropDownListFilterChangeEvent,
    DropDownListHandle,
    DropDownListProps,
    ListItemProps
} from "@progress/kendo-react-dropdowns";
import {DropDownListItem} from "../../utils/Models";
import React, {cloneElement, ReactElement, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {InputHandle, MaskedTextBoxEvent} from "@progress/kendo-react-inputs";
import {Error} from "@progress/kendo-react-labels";
import {CompositeFilterDescriptor, filterBy, FilterDescriptor} from "@progress/kendo-data-query";
import {GUID} from "../../utils/AppConstants";


export default function FlintDropDownList<T extends DropDownListItem>(props: FlintDropDownListProps<T>) {
    const [visited, setVisited] = useState(false);
    const [selectedValueState, setSelectedValueState] = useState<{ value: T } | undefined>(undefined);
    const [displayedData, setDisplayedData] = useState<T[] | undefined>(props.dataSource?.slice());
    //const inputRef = useRef<DropDownListHandle | null>(null); // Corrected type for Kendo DropDownList ref
    const handleOnBlur = (event: DropDownListBlurEvent) => {
        setVisited(true);
        validationData?.validateFunction?.(validationData?.optionalObjectToValidate).catch((err) => {
        });
        if (props.onBlur) {
            props.onBlur(event); // Call the parent's onBlur handler if provided
        }
    };

    //console.log(`Dropdown list re-rendered. Label: ${props.label}`);
    const {t} = useTranslation();
    const {
        validationData,
        onBlur,
        wrapperClassName = `dropdown-wrapper${validationData?.validationMessage ? " k-invalid" : ""}`,
        translateName,
        useHtmlTemplate,
        dataSource,
        selectedValue,
        usePrimitives = false, // Default to `false` if not provided
        onItemSelected,
        defaultItem,
        onFilterChange = handleFilterChange,
        style = {minWidth: "100px"}, // Default style
        dataItemKey = "Id", // Default key for data items
        textField = "Name", // Default text field
        ...other
    } = props;
    const defaultListItem = !defaultItem ? undefined : (typeof defaultItem === "string" ? {
        Id: 0,
        Name: defaultItem
    } : defaultItem);

    useEffect(() => {
        setDisplayedData(props.dataSource?.slice());
    }, [props.dataSource])

    useEffect(() => {
        if (!selectedValue && selectedValue !== 0) {
            setSelectedValueState({value: defaultListItem || ({} as any)});
            return;
        }

        if (usePrimitives) {
            const selection = dataSource?.find((x) => x.Id === selectedValue 
                || ("Guid" in x  && x.Guid == selectedValue)) as T;
            setSelectedValueState({value: selection || defaultListItem || ({} as any)});
            return;
        }
        setSelectedValueState({value: selectedValue as T});
    }, [dataSource, selectedValue]);

    function getFilterDescriptor(s: string) {
        return {
            logic: 'or',
            filters: [
                {field: "Name", operator: "contains", value: s, ignoreCase: true},
                {field: "IdString", operator: "contains", value: s, ignoreCase: true}
            ]
        }
    }

    function filterData(searchString: string) {
        if (!dataSource)
            return [];
        const data = dataSource?.slice();
        const f = getFilterDescriptor(searchString) as any;
        return filterBy(data, f)

    }

    function handleFilterChange(event: DropDownListFilterChangeEvent) {
        if (props.filterable)
            setDisplayedData(filterData(event.filter.value));
    }

    function handleOnChange(event: DropDownListChangeEvent) {
        const newSelection = event.target.value as T;
        setSelectedValueState({value: newSelection});
        onItemSelected?.(newSelection); // Call `onItemSelected` if provided
        props.onChange?.(event);
    }

    const itemRenderTranslation = (li: ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
        const ddlText = t(itemProps.dataItem.Name);
        const itemChildren = (
            <span key={itemProps.index}>{ddlText}</span>
        );
        return cloneElement(li, li.props, itemChildren);
    };
    
    const itemRenderHtml = (li: ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
        const itemChildren = (
            <span key={itemProps.index} dangerouslySetInnerHTML={{__html:itemProps.dataItem.Name}}/>
        );
        return cloneElement(li, li.props, itemChildren);
    };
    const valueRenderTranslation = (element: ReactElement<HTMLSpanElement>, value: any) => {
        if (!value) {
            return element;
        }
        const text = t(value.Name);
        const children = [
            <span key={1}>{text}</span>
        ];

        return cloneElement(element, {...element.props}, children);
    };
    
    const valueRenderHtml = (element: ReactElement<HTMLSpanElement>, value: any) => {
        if (!value) {
            return element;
        }
        const children = [
            <span key={1} dangerouslySetInnerHTML={{__html:value.Name}}/>
        ];

        return cloneElement(element, {...element.props}, children);
    };
    return (
        <div className={wrapperClassName}>
            <DropDownList
                itemRender={props.translateName ? itemRenderTranslation : (props.useHtmlTemplate ? itemRenderHtml : props.itemRender)}
                valueRender={props.translateName ? valueRenderTranslation : (props.useHtmlTemplate ? valueRenderHtml : props.valueRender)}
                data={displayedData}
                style={style}
                value={selectedValueState?.value}
                dataItemKey={dataItemKey}
                textField={textField}
                defaultItem={defaultListItem}
                onFilterChange={onFilterChange}
                onBlur={handleOnBlur}
                onChange={handleOnChange}
                {...other}
            />
            {/*{visited && validationMessage && <Error>{validationMessage}</Error>}*/}
            {validationData?.validationMessage != null && <Error>{validationData?.validationMessage}</Error>}
        </div>
    );
}

export interface FlintDropDownListProps<T extends DropDownListItem> extends DropDownListProps {
    wrapperClassName?: string;
    dataSource: T[] | undefined;
    onItemSelected?: (item: T) => void;
    selectedValue?: T | number | GUID | undefined;
    usePrimitives?: boolean; // Indicates whether primitives are used for selection
    translateName?: boolean; // TRUE if the Name field values should be translated
    useHtmlTemplate? : boolean; // TRUE if order item names contain HTML tags
    validationData?: {
        validationMessage?: string | undefined,
        validateFunction?: (objectToValidate?: any) => Promise<void>,
        optionalObjectToValidate?: any
    }
    // default item will have Id = 0 and, when selected, will return null value
    defaultItem?: T | string;
}
